import { useCallback, useMemo } from "react"
import { pick } from "lodash"
import { SelectedTypedInstrument } from "helpers/types/instruments/selected"
import { IsoDate } from "helpers/types/dates"
import { InstrumentsMetadata } from "connected-components/dashboard/context/types"
import { usePositionSetMetadata } from "resources/position-sets"
import { SHORT } from "resources/position-sets/composition"
import { BaseInstrumentWithName } from "../types"

interface UseOptimizeBaseParams {
  modelId: string
  endDate: IsoDate
  isBaseOverridden: boolean
  baseOverride: BaseInstrumentWithName | null
  dashboardBase: SelectedTypedInstrument | null
  instrumentsMetadata?: InstrumentsMetadata
}

interface UseOptimizeBaseReturn {
  optimizerBase: (BaseInstrumentWithName & { weight: number }) | null
  optimizerBaseForQueries: SelectedTypedInstrument | null
}

/**
 * Custom hook to manage optimizer base instrument with proper weight calculation.
 *
 * Handles:
 * - Fetching metadata for override base to determine weight
 * - Calculating weight based on composition type (SHORT = -1, LONG = 1)
 * - Using dashboard base weight when override matches dashboard base
 * - Stripping display properties (name) for GraphQL queries
 */
export function useOptimizeBase({
  modelId,
  endDate,
  isBaseOverridden,
  baseOverride,
  dashboardBase,
  instrumentsMetadata,
}: UseOptimizeBaseParams): UseOptimizeBaseReturn {
  // Determine if we need to fetch metadata for the override base
  // Only when override is enabled AND it doesn't match the dashboard base
  const needsOverrideMetadata = useMemo(() => {
    if (!isBaseOverridden || !baseOverride) return false
    if (!dashboardBase) return true
    return baseOverride.id !== dashboardBase.id || baseOverride.type !== dashboardBase.type
  }, [isBaseOverridden, baseOverride, dashboardBase])

  // Pass the raw baseOverride (without weight) to avoid circular dependencies
  const [baseOverrideMetadata] = usePositionSetMetadata(
    modelId,
    needsOverrideMetadata && baseOverride ? baseOverride : undefined,
    endDate,
  )

  // Helper function to calculate weight from metadata (same logic as dashboard)
  const getInstrumentWeight = useCallback((metadata: { compositionType?: string } | null | undefined) => {
    return metadata?.compositionType === SHORT ? -1 : 1
  }, [])

  // Derive the actual base to use: baseOverride when override is ON, dashboard base otherwise
  const optimizerBase = useMemo(() => {
    if (isBaseOverridden) {
      if (!baseOverride) return null

      // If override matches dashboard base, use dashboard base's weight
      const isDashboardBase = dashboardBase && baseOverride.id === dashboardBase.id && baseOverride.type === dashboardBase.type
      if (isDashboardBase) {
        const weight = "weight" in dashboardBase ? (dashboardBase.weight as number) : 1
        return { ...baseOverride, weight } as BaseInstrumentWithName & { weight: number }
      }

      // Calculate weight from override base's metadata
      const weight = baseOverrideMetadata ? getInstrumentWeight(baseOverrideMetadata) : 1
      return { ...baseOverride, weight } as BaseInstrumentWithName & { weight: number }
    }

    if (dashboardBase) {
      const name = instrumentsMetadata?.base?.name || dashboardBase.id
      return { ...dashboardBase, name } as BaseInstrumentWithName & { weight: number }
    }

    return null
  }, [isBaseOverridden, baseOverride, dashboardBase, instrumentsMetadata, baseOverrideMetadata, getInstrumentWeight])

  // Strip name property when passing to queries (only keep id, type, weight and experimentId)
  const optimizerBaseForQueries = useMemo(() => {
    if (!optimizerBase) return null
    return pick(optimizerBase, ["id", "type", "weight", "experimentId"]) as SelectedTypedInstrument
  }, [optimizerBase])

  return {
    optimizerBase,
    optimizerBaseForQueries,
  }
}
